import React, { useState, useContext, useEffect, createRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import AuthSplitLayout from "../../components/Auth/AuthSplitLayout";
import ModernAuthField from "../../components/Auth/ModernAuthField";

function Login() {
	const [user, setUser] = useState({ email: "", password: "" });
	const [showPassword, setShowPassword] = useState(false);
	const [captcha, setCaptcha] = useState("");

	const recaptchaRef = createRef();
	const { handleLogin } = useContext(AuthContext);

	useEffect(() => {
		if (recaptchaRef?.current) recaptchaRef.current.reset();
	}, []);

	const handleChangeInput = (e) => {
		setUser({ ...user, [e.target.name]: e.target.value });
	};

	const handlSubmit = (e) => {
		e.preventDefault();
		handleLogin(user);
	};

	const captchaRequired = Boolean(process.env.REACT_APP_RECAPTCHA_SITE_KEY);

	return (
		<AuthSplitLayout testId="login-page">
			<div className="mb-8">
				<h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
					{i18n.t("login.title")}
				</h1>
				<p className="mt-1.5 text-sm text-zinc-500">{i18n.t("login.subtitle")}</p>
			</div>

			<form noValidate onSubmit={handlSubmit} className="space-y-4">
				<ModernAuthField
					id="email"
					label={i18n.t("login.form.email")}
					type="email"
					name="email"
					placeholder={i18n.t("login.form.emailPlaceholder")}
					value={user.email}
					onChange={handleChangeInput}
					autoComplete="email"
					autoFocus
					required
				/>

				<div>
					<ModernAuthField
						id="password"
						label={i18n.t("login.form.password")}
						type={showPassword ? "text" : "password"}
						name="password"
						placeholder={i18n.t("login.form.passwordPlaceholder")}
						value={user.password}
						onChange={handleChangeInput}
						autoComplete="current-password"
						required
						suffix={
							<button
								type="button"
								onClick={() => setShowPassword((prev) => !prev)}
								className="text-xs font-medium text-zinc-400 transition-colors hover:text-zinc-700"
							>
								{showPassword
									? i18n.t("login.buttons.hidePassword")
									: i18n.t("login.buttons.showPassword")}
							</button>
						}
					/>
					<div className="mt-1.5 flex justify-end">
						<RouterLink
							to="/recoverPassword"
							className="text-xs text-zinc-500 transition-colors hover:text-zinc-900"
						>
							{i18n.t("login.buttons.recover")}
						</RouterLink>
					</div>
				</div>

				{captchaRequired && (
					<div className="flex justify-center overflow-hidden">
						<ReCAPTCHA
							style={{ transform: "scale(0.85)", transformOrigin: "center" }}
							ref={recaptchaRef}
							name="recaptcha"
							id="recaptcha"
							sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
							secretkey={process.env.REACT_APP_RECAPTCHA_SECRET_KEY}
							onChange={() => setCaptcha(true)}
							onExpired={() => {
								recaptchaRef.current.reset();
							}}
							theme="light"
						/>
					</div>
				)}

				<button
					type="submit"
					className="mt-2 w-full rounded-lg bg-zinc-950 py-2.5 text-sm font-medium text-white transition-all duration-150 hover:bg-zinc-800 active:scale-[0.99] disabled:opacity-60"
					disabled={Boolean(captchaRequired && !captcha)}
				>
					{i18n.t("login.buttons.submit")}
				</button>
			</form>

			<p className="mt-8 text-center text-sm text-zinc-500">
				{i18n.t("login.buttons.registerPrefix")}{" "}
				<RouterLink
					to="/signup"
					className="font-medium text-zinc-900 underline-offset-2 hover:underline"
				>
					{i18n.t("login.buttons.registerLink")}
				</RouterLink>
			</p>
		</AuthSplitLayout>
	);
}

export default Login;
